import React, { useState, useEffect } from 'react';
import { Gallery, DisplaySettings } from '../../types/Gallery';
import { loadLiveCategories } from '../../utils/CategoryAPI';
import ImageCard from './ImageCard';
import GalleryModal from './GalleryModal';
import './NewMasonryGallery.css';

interface NewMasonryGalleryProps {
  galleries: Gallery[];
  settings: DisplaySettings;
}

const NewMasonryGallery: React.FC<NewMasonryGalleryProps> = ({ galleries, settings }) => {
  // Ensure galleryCount doesn't exceed actual available galleries
  const maxGalleries = settings.galleryCount || galleries.length;
  const actualGalleryCount = Math.min(maxGalleries, galleries.length);
  const displayGalleries = galleries.slice(0, actualGalleryCount);
  const [selectedGallery, setSelectedGallery] = useState<Gallery | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);

  const handleGalleryClick = (gallery: Gallery) => {
    setSelectedGallery(gallery);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedGallery(null);
  };

  // Load categories
  useEffect(() => {
    const loadCategoriesData = async () => {
      try {
        const categoriesData = await loadLiveCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    loadCategoriesData();

    const handleCategoryUpdate = () => {
      setTimeout(() => {
        loadCategoriesData();
      }, 100);
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'admin_categories') {
        setTimeout(() => {
          loadCategoriesData();
        }, 100);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('category_updated', handleCategoryUpdate);
    window.addEventListener('categories_updated', handleCategoryUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('category_updated', handleCategoryUpdate);
      window.removeEventListener('categories_updated', handleCategoryUpdate);
    };
  }, []);

  // Get category color
  const getCategoryColor = (categoryName: string): string => {
    const normalizedInput = (categoryName || '').toLowerCase().trim();
    const category = categories.find(cat =>
      (cat.name || '').toLowerCase().trim() === normalizedInput ||
      (cat.slug || '').toLowerCase().trim() === normalizedInput
    );
    return category?.color || '#8B4513';
  };

  // Apply theme settings
  useEffect(() => {
    const loadThemeAndApply = () => {
      const currentTheme = localStorage.getItem('admin-theme');
      if (currentTheme) {
        try {
          const themeData = JSON.parse(currentTheme);
          const root = document.documentElement;

          if (themeData.palette) {
            root.style.setProperty('--color-primary', themeData.palette.primary || '#8B4513');
            root.style.setProperty('--color-secondary', themeData.palette.secondary || '#A0522D');
            root.style.setProperty('--color-accent', themeData.palette.accent || '#D4AF37');
            root.style.setProperty('--color-background', themeData.palette.background || '#FFFFFF');
            root.style.setProperty('--color-text', themeData.palette.text || '#8B4513');
            root.style.setProperty('--color-surface', themeData.palette.surface || '#F8F9FA');
            root.style.setProperty('--color-border', themeData.palette.border || '#E1E1E1');
          }

          const isChristmas = themeData.presetId === 'christmas-festive';
          if (isChristmas) {
            document.body.classList.add('christmas-gallery-active');
          } else {
            document.body.classList.remove('christmas-gallery-active');
          }
        } catch (error) {
          console.error('Error loading theme settings:', error);
        }
      }
    };

    loadThemeAndApply();

    const handleThemeChange = () => {
      loadThemeAndApply();
    };

    window.addEventListener('storage', handleThemeChange);
    window.addEventListener('theme_updated', handleThemeChange);
    
    return () => {
      window.removeEventListener('storage', handleThemeChange);
      window.removeEventListener('theme_updated', handleThemeChange);
      document.body.classList.remove('christmas-gallery-active');
    };
  }, []);

  if (displayGalleries.length === 0) {
    return (
      <div className="new-masonry-container">
        <div className="no-galleries">
          <h2>Portfolio Coming Soon</h2>
          <p>My photography collections are being prepared with love and artistry...</p>
        </div>
      </div>
    );
  }

  // Dynamic sizing based on gallery count - fewer galleries = larger items
  const galleryCount = displayGalleries.length;
  const getGapSize = () => {
    if (galleryCount === 1) return '2rem';
    if (galleryCount === 2) return '1.75rem';
    if (galleryCount === 3) return '1.5rem';
    if (galleryCount <= 6) return '1.25rem';
    return '1rem';
  };

  const getPaddingSize = () => {
    if (galleryCount === 1) return '2rem';
    if (galleryCount === 2) return '1.75rem';
    if (galleryCount === 3) return '1.5rem';
    return '1.25rem';
  };

  const getColumns = () => {
    if (galleryCount === 1) return 1;
    if (galleryCount === 2) return 2;
    if (galleryCount === 3) return 2;
    if (galleryCount <= 6) return 3;
    if (galleryCount <= 12) return 4;
    return 5;
  };

  return (
    <>
      <div 
        className="new-masonry-container"
        style={{
          '--gap-size': getGapSize(),
          '--padding-size': getPaddingSize(),
        } as React.CSSProperties}
        data-count={galleryCount}
      >
        <div 
          className="new-masonry-grid"
          style={{
            gridTemplateColumns: `repeat(${getColumns()}, 1fr)`,
            gap: getGapSize(),
            padding: getPaddingSize(),
          }}
        >
          {displayGalleries.map((gallery, index) => (
            <ImageCard 
              key={gallery.id} 
              gallery={gallery}
              showcase={true}
              feature={index === 0}
              className="new-masonry-item"
              onGalleryClick={handleGalleryClick}
              categoryColor={getCategoryColor(gallery.category)}
            />
          ))}
        </div>
      </div>
      
      <GalleryModal 
        gallery={selectedGallery}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </>
  );
};

export default React.memo(NewMasonryGallery);
